import React, { useContext, useEffect, useRef, useState } from 'react';
import { ReportContext } from '../../context/ReportContext';
import { ReportDataService } from '../../services/reportDataService';
import { formatDate } from '../../services/sharedService';
import { formatDisplayDate } from '../../services/dateUtils';
import PdfViewer from '../common/PdfViewer';
import ConfirmationModal from '../ConfirmationModal';
import { Post } from '../../models/post';
import { HeaderField } from '../../models/reportTemplate';
import {
  StaleReason, staleTier, stalePillLabel, staleTooltip, staleDetails,
  resolveFieldEntry, changedFieldEntries,
} from '../../models/reportStaleness';
import HelpAsk from '../../help/HelpAsk';
import { useOfflineActionTitle } from '../../hooks/useConnectivity';

/** An existing generated report for the current child/period/template. */
export interface ExistingReportInfo {
  url: string;
  createdAt: string | null;
  createdByName: string | null;
  /** The password the stored PDF was generated with, so the inline viewer can
   *  open a protected report without prompting. Captured at fetch/generate
   *  time — not the panel's live password input, which may have been edited
   *  since without regenerating. */
  password?: string;
  /** The header-field entries the stored PDF was written with. Kept for the
   *  same reason as `password` above: the panel's own fieldValues double as the
   *  edit buffer, so once someone types, what the file actually used is gone.
   *  This is the copy that survives, and the only thing "you have unsaved
   *  changes" can be measured against. */
  fieldValues?: Record<string, string>;
  /** Why the stored report no longer matches its sources — server-computed, so
   *  this bay and the History pill can't disagree. Absent on a report just
   *  generated in this session (nothing has had a chance to drift yet). */
  staleReasons?: StaleReason[];
}

interface GenerateReportPanelProps {
  childId: string | null;
  startDate: string | null;
  endDate: string | null;
  posts?: Post[];
  /** Unpublished posts in this period — they will NOT appear in the report, so
   *  the pre-flight names them before generating. */
  draftCount?: number;
  /** The stored report for this identity, if any — owned by BuildReportPanel
   *  (which also hydrates fieldValues/password from it). */
  existing?: ExistingReportInfo | null;
  checkingExisting?: boolean;
  /** Reports a successful generation so the parent can update `existing`. */
  onGenerated?: (info: ExistingReportInfo) => void;
  /** Report Builder template identity — a row id, or 'stock' for the code default. */
  templateId?: number | 'stock';
  /** Generation-editable fields (manual and derived) — required ones gate the Generate button. */
  editableFields?: HeaderField[];
  /** Untouched-field pre-fills by key (template default or computed value),
   *  resolved by the parent — what an untouched required field counts as. */
  fieldDefaults?: Record<string, string>;
  /** Child name for the inline viewer's title bar. */
  childName?: string;
  /** Switches the column to the Report Details fields. Passed only when
   *  another pane is open — that's the only time the offer means anything. */
  onEditFields?: () => void;
  onGenerateSuccess?: (message: string) => void;
}

/**
 * The press bay: everything about the report *file* — its state, who made it
 * and when, and the actions that produce or open it. Deliberately a surface of
 * its own, pinned to the bottom of the Report Details column, so generating is
 * never something the user scrolls past at the end of a field list.
 */
const GenerateReportPanel: React.FC<GenerateReportPanelProps> = ({
  childId, startDate, endDate, posts, draftCount = 0, existing = null, checkingExisting = false, onGenerated,
  templateId = 'stock', editableFields = [], fieldDefaults = {}, childName, onEditFields,
  onGenerateSuccess,
}) => {
  const { reportDate, showHeader, pdfPassword, setPdfPassword, fieldValues } = useContext(ReportContext);
  const [isGenerating, setIsGenerating] = useState(false);
  const offlineTitle = useOfflineActionTitle();
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Most reports never get a password, and the bay is on screen the whole time
  // now — so on a not-yet-generated report the field stays folded behind a chip
  // until it's wanted. Opens itself when a stored password arrives, and stays
  // open once opened (so clearing the field to remove a password doesn't yank
  // it away mid-edit).
  const [passwordOpen, setPasswordOpen] = useState(false);
  useEffect(() => {
    if (pdfPassword && !existing) setPasswordOpen(true);
  }, [pdfPassword, existing]);

  // The regenerate step on a stored report. Entering it is what reveals the
  // password field — a PDF can only be encrypted as it's written, so that's
  // the only moment the setting can actually do anything.
  const [regenerating, setRegenerating] = useState(false);
  const [showPreflight, setShowPreflight] = useState(false);
  // A new child/period/template is a different report; drop out of the step.
  useEffect(() => { setRegenerating(false); }, [existing?.url]);

  // The bay opens and closes entirely in CSS (:hover / :focus-within on
  // .report-press), so React is never told that it collapsed — a regenerate step
  // or password offer left open stayed open in state, and was still sitting
  // there the next time the bay was raised. Clicking away is the deliberate "done
  // with this" gesture, so it puts the bay back to how it opens by default.
  //
  // Listening only while something is actually open, so there's no document-wide
  // handler running for a bay nobody has touched.
  const pressRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!regenerating && !passwordOpen && !showPassword) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target || pressRef.current?.contains(target)) return;
      // The pre-flight dialog and the PDF viewer are siblings of the bay, so by
      // DOM containment a click on either is "outside" while plainly still part
      // of the same piece of work.
      if (showPreflight || viewing) return;
      if (target instanceof Element && target.closest('.modal')) return;
      setRegenerating(false);
      setPasswordOpen(false);
      setShowPassword(false);
      // Back to the stored report's password. A password typed but never
      // generated with would otherwise stay in state, invisible, and be applied
      // by the next generate — which matters more than the few keystrokes lost.
      setPdfPassword(existing?.password ?? '');
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [regenerating, passwordOpen, showPassword, showPreflight, viewing, existing?.password, setPdfPassword]);

  const showPasswordField = existing ? regenerating : passwordOpen;

  // A required field counts as filled by an entry or by its pre-fill.
  const missingRequired = editableFields.filter(f =>
    f.required && !resolveFieldEntry(f, fieldValues, fieldDefaults)
  );

  // With Review and Generate sharing one workspace there is no separate
  // empty-state screen any more — an empty report is instead expressed as a
  // disabled Generate button plus the hint below.
  const publishedCount = posts?.length ?? 0;
  const hasPublishedPosts = publishedCount > 0;

  // A report can be generated once we have the child, the date range, at
  // least one published post, and every required manual field (ActionPanel
  // marks the missing ones inline).
  const canGenerate =
    !!(childId && startDate && endDate) && hasPublishedPosts && missingRequired.length === 0;

  // ── Pre-flight ────────────────────────────────────────────────────────────
  // What's worth saying before writing the PDF. Required fields aren't here:
  // they already block generation outright and the status line names them.
  // These are the quiet omissions — a draft that won't appear, an optional
  // header field nobody filled in because they never opened Report Details.
  const emptyOptionalFields = editableFields.filter(f =>
    !f.required && !resolveFieldEntry(f, fieldValues, fieldDefaults)
  );

  const preflightNotes: string[] = [];
  if (draftCount > 0) {
    preflightNotes.push(
      `${draftCount} post${draftCount === 1 ? ' is' : 's are'} still in draft and will not appear in this report.`
    );
  }
  if (emptyOptionalFields.length > 0) {
    preflightNotes.push(
      `Not filled in: ${emptyOptionalFields.map(f => f.label).join(', ')}.`
    );
  }
  // Nothing to flag → generating is unremarkable, so it just happens. A prompt
  // that fires every time (and often says "all good") is one people learn to
  // dismiss unread, which would cost us the times it matters.
  const hasPreflight = preflightNotes.length > 0;

  // Whether the pre-flight's secondary action should offer the fields. Keyed
  // ONLY on the fields being what's flagged: when the sole note is about drafts,
  // "Review details" would send the reader to a pane with nothing wrong on it —
  // but whenever a field IS named, the offer must be there to act on.
  const offerFieldReview = emptyOptionalFields.length > 0 && !!onEditFields;

  // Staleness is the server's answer now (see models/reportStaleness.ts). It
  // used to be recomputed here from the loaded posts' timestamps, which could
  // only ever notice post edits — never a template change, a photo added or
  // held back from the report, blurring applied, or updated branding. It also
  // leaned on post.updated_at, which bumps for title and classroom edits the
  // report never renders, so it cried stale over changes that changed nothing.
  const staleReasons = existing?.staleReasons ?? [];
  const tier = staleTier(staleReasons);
  // Only content drift earns "Out of date" — that phrasing pushes the reader to
  // regenerate, which a dated logo does not warrant.
  const needsAttention = tier === 'content';

  // Generate, once anything worth flagging has been seen. The bar's button goes
  // through requestGenerate; the pre-flight's confirm and the regenerate step
  // (which shows its warnings inline, so it has already done the telling) call
  // this directly.
  const handleGenerate = async () => {
    setShowPreflight(false);
    if (!childId || !startDate || !endDate) return;

    setIsGenerating(true);
    setError(null);

    try {
      const result = await ReportDataService.generateReport({
        childId: parseInt(childId),
        startDate,
        endDate,
        reportDate: reportDate || undefined,  // send user's chosen display date, or let server default to endDate
        showHeader,
        pdfPassword: pdfPassword || undefined,
        templateId,
        // Always sent, even empty — {} means "explicitly no entries", which
        // stops the server reusing the replaced report's stored values.
        fieldValues,
      });
      onGenerated?.({
        url: result.url,
        createdAt: result.createdAt ?? null,
        createdByName: result.createdByName ?? null,
        // The PDF was just written with whatever the password input held.
        password: pdfPassword || undefined,
        // Snapshot, not a reference: these entries are now what the file was
        // built with, so they become the baseline the "unsaved changes" hint
        // measures against. Sharing the live object would make every future
        // edit invisible to it.
        fieldValues: { ...fieldValues },
      });
      // The step is done — back to View / Download on the new file.
      setRegenerating(false);
      onGenerateSuccess?.(result.templateMissing
        ? 'Report generated with the default Weekly Report — the selected template no longer exists.'
        : 'Report generated successfully!');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  const requestGenerate = () => {
    if (hasPreflight) { setShowPreflight(true); return; }
    void handleGenerate();
  };

  // The stored PDF was written with the password held in `existing`; editing
  // the input changes what the NEXT generation uses, not the file on S3. Only
  // worth saying inside the regenerate step, where the field is visible.
  const passwordChanged = regenerating && !!existing && pdfPassword !== (existing.password ?? '');

  // Same story for the header fields, and more easily missed: the live preview
  // redraws as they're typed, so the workspace shows a version of the report
  // that isn't the file on S3 — with nothing saying so.
  //
  // See changedFieldEntries for why both sides are resolved rather than
  // compared as raw entries — it's what makes "changed it back" clear again.
  const changedFields = existing
    ? changedFieldEntries(editableFields, fieldValues, existing.fieldValues ?? {}, fieldDefaults)
    : [];

  const periodLabel = startDate && endDate
    ? `${formatDisplayDate(startDate)} → ${formatDisplayDate(endDate)}`
    : null;

  // One line under the eyebrow, in priority order: what's blocking, what's
  // stored, or what's ready to go.
  const statusLine = (): React.ReactNode => {
    if (!hasPublishedPosts) return 'No published posts in this period yet.';
    if (missingRequired.length > 0) {
      const names = missingRequired.map(f => `“${f.label}”`).join(', ');
      return (
        <>
          Fill in {names} to generate.
          {onEditFields && (
            <> <button type="button" className="report-press__link" onClick={onEditFields}>Open Report Details</button></>
          )}
        </>
      );
    }
    if (existing) {
      return (
        <>
          Generated by <strong>{existing.createdByName}</strong>
          {existing.createdAt && <> · {formatDate(existing.createdAt)}</>}
        </>
      );
    }
    return `${publishedCount} published post${publishedCount === 1 ? '' : 's'}${periodLabel ? ` · ${periodLabel}` : ''}`;
  };

  // The chip names what drifted rather than just that something did, and stays
  // quiet for cosmetic drift — the report is still correct, so it reads as a
  // note beside "Up to date" rather than replacing it with an alarm.
  //
  // It also has to carry unsaved field edits. The bay minimises down to the bar
  // row, which clips the hint lines away, leaving the chip as the ONLY status
  // the reader can see — and "Up to date" beside a workspace showing edits the
  // PDF doesn't have is worse than silence. Ordered by what to do about it:
  // content drift, then unsaved edits, then cosmetic drift. A report that is
  // both stale AND edited still just needs regenerating, so the stale reason
  // keeps the chip and the edits show in the hint when the bay is open.
  const stateChip = existing
    ? (needsAttention
        ? <span className="report-press__state report-press__state--stale" title={staleTooltip(staleReasons)}>
            <span className="report-press__dot" />{stalePillLabel(staleReasons)}
          </span>
        : changedFields.length > 0
          ? <span
              className="report-press__state report-press__state--stale"
              title={`${changedFields.map(f => f.label).join(', ')} `
                + `${changedFields.length === 1 ? 'has' : 'have'} been changed since this report was generated. `
                + `Regenerate to apply ${changedFields.length === 1 ? 'it' : 'them'} to the PDF.`}
            >
              <span className="report-press__dot" />Details edited
            </span>
          : tier === 'cosmetic'
            ? <span className="report-press__state report-press__state--cosmetic" title={staleTooltip(staleReasons)}>
                <span className="report-press__dot" />{stalePillLabel(staleReasons)}
              </span>
            : <span className="report-press__state report-press__state--current"><span className="report-press__dot" />Up to date</span>)
    : <span className="report-press__state report-press__state--none">Not generated</span>;

  // Nothing published and nothing stored — there is no file to act on, so the
  // bay collapses to the one line that says what unlocks it. Hiding it outright
  // would leave the column with no answer to "where do I generate the report?".
  if (!hasPublishedPosts && !existing) {
    return (
      <div className="report-press report-press--idle">
        <p className="report-press__status">
          <i className="fas fa-circle-info" />
          Publish a post to build the report — the PDF is generated from published posts.
        </p>
      </div>
    );
  }

  // The bar is three rows, revealed in order as they become relevant:
  //
  //   1. what this is, what state it's in, and the everyday actions — the only
  //      row visible while the bay is minimised
  //   2. the receipt, and the way into regenerating
  //   3. the regenerate prompt itself, once asked for
  //
  // Each row is built here rather than inline so the order above is legible in
  // the markup instead of buried in three nested ternaries.
  // Nothing while the regenerate prompt is up: the file View and Download
  // would open is the one about to be replaced, and the prompt carries its own
  // confirm and cancel. The row goes with them rather than sitting empty.
  const everydayActions = regenerating ? null : existing ? (
    <>
      <button
        type="button"
        className="report-press__btn report-press__btn--primary"
        onClick={() => setViewing(true)}
      >
        <i className="fas fa-eye" />View
      </button>
      <a
        href={existing.url}
        // target="_blank" + rel="noopener noreferrer" is essential: `url` is a
        // cross-origin presigned S3 URL, so browsers ignore the `download`
        // attribute on it and would otherwise navigate the current tab to the
        // PDF, replacing the page the user was on.
        target="_blank"
        rel="noopener noreferrer"
        download
        className="report-press__btn"
      >
        <i className="fas fa-arrow-down" />Download
      </a>
    </>
  ) : (
    <button
      type="button"
      className="report-press__btn report-press__btn--primary"
      data-help="reports.generate"
      onClick={requestGenerate}
      disabled={!canGenerate || isGenerating || checkingExisting || !!offlineTitle}
      title={offlineTitle}
    >
      {isGenerating
        ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Generating…</>
        : <><i className="fas fa-file-arrow-down" />Generate report</>}
    </button>
  );

  const passwordField = (
    <div className="report-press__password">
      <i className="fas fa-lock report-press__lock" />
      <input
        type={showPassword ? 'text' : 'password'}
        className="report-press__password-input"
        placeholder="PDF password (optional)"
        aria-label="PDF password"
        // This is NOT a login field — it's the key the PDF gets encrypted with.
        // Left to its own devices the browser treats a focused type=password as
        // a sign-in box and offers a saved site credential, which would encrypt
        // a child's report with the user's real account password AND store it
        // (in data.report.encrypted_password) to be shown back on download.
        // "new-password" is the value browsers honour for "never suggest a
        // stored credential here"; autoComplete="off" is widely ignored on
        // password inputs.
        autoComplete="new-password"
        autoFocus
        value={pdfPassword}
        onChange={(e) => setPdfPassword(e.target.value)}
        // Left empty and stepped away from, the offer folds back into its chip
        // — an empty field that stays open is just a control asking to be
        // ignored, and it costs the bar a row. Only the standalone offer does
        // this: inside the regenerate prompt the field belongs to the prompt,
        // which has its own Cancel.
        onBlur={(e) => {
          if (existing || pdfPassword.trim()) return;
          // Moving to the reveal toggle beside it is still using the field.
          const next = e.relatedTarget as Node | null;
          if (next && e.currentTarget.parentElement?.contains(next)) return;
          setShowPassword(false);
          setPasswordOpen(false);
        }}
      />
      <button
        type="button"
        className="report-press__reveal"
        onClick={() => setShowPassword(p => !p)}
        title={showPassword ? 'Hide password' : 'Show password'}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} />
      </button>
    </div>
  );

  return (
    <div className="report-press" ref={pressRef}>
      {/* Anchored to the bottom and clipped to the actions row's height, so it
          grows upward over the workspace on hover. The actions are the last
          child deliberately: they hold their position whether the panel is
          open or shut, so hovering a button doesn't move it out from under
          the pointer that's hovering it. */}
      <div className="report-press__sheet">
        <div className="report-press__row report-press__row--detail">
          <p className="report-press__status">
            {statusLine()}
            {/* The two reasons generation is blocked each have a guide: no
                published posts (publish one) and unfilled required fields
                (the report guide's Report Details step). */}
            {!hasPublishedPosts && (
              <HelpAsk
                guideKey="posts.publish-post"
                label="How do I publish a post?"
                variant="inline"
                className="ms-2"
              />
            )}
            {hasPublishedPosts && missingRequired.length > 0 && (
              <HelpAsk
                guideKey="reports.generate-pdf"
                label="How do I fill these in?"
                variant="inline"
                className="ms-2"
              />
            )}
          </p>

          {existing && !regenerating && (
            <button
              type="button"
              // Accented for unsaved edits too, not just server-side drift:
              // either way regenerating is the thing to do next.
              className={`report-press__quiet ${needsAttention || changedFields.length > 0 ? 'is-accent' : ''}`}
              onClick={() => setRegenerating(true)}
              disabled={checkingExisting}
            >
              <i className="fas fa-rotate" />Regenerate
            </button>
          )}

          {/* Nothing stored yet: generating is the pending act, so the password
              is offered here rather than behind the regenerate step. */}
          {!existing && !passwordOpen && (
            <button type="button" className="report-press__chip" onClick={() => setPasswordOpen(true)}>
              <i className="fas fa-lock" />Add a password
            </button>
          )}
        </div>

        {/* Spells out every reason rather than the old blanket "posts have
            changed" — which was often simply untrue (a template edit, an
            excluded photo) and told the reader nothing about what to check.
            Cosmetic drift drops the warning icon and the call to regenerate. */}
        {existing && tier && (
          <div className={`report-press__alert${needsAttention ? '' : ' report-press__alert--quiet'}`}>
            <i className={`fas ${needsAttention ? 'fa-exclamation-triangle' : 'fa-palette'}`} />
            <span>
              {staleDetails(staleReasons).length === 1 ? (
                staleDetails(staleReasons)[0]
              ) : (
                <ul className="report-press__alert-list">
                  {staleDetails(staleReasons).map(d => <li key={d}>{d}</li>)}
                </ul>
              )}
              {needsAttention && ' Regenerate to bring it up to date.'}
            </span>
          </div>
        )}

        {/* The regenerate prompt. A PDF can only be encrypted as it's written,
            so this is the only moment the password can do anything — which is
            why it appears with the confirm rather than sitting on the bar. */}
        {/* Regenerating is where drafts are most likely to have piled up since
            last time, so it gets the same pre-flight — inline rather than as a
            modal, because stacking a dialog on a prompt that's already open is
            clumsy. Shown here, so handleGenerate can be called directly below
            without a second confirmation. */}
        {existing && regenerating && hasPreflight && (
          <div className="report-press__preflight">
            {preflightNotes.map(note => (
              <p key={note}><i className="fas fa-triangle-exclamation" />{note}</p>
            ))}
            {offerFieldReview && (
              <button type="button" className="report-press__link" onClick={onEditFields}>
                Review report details
              </button>
            )}
          </div>
        )}

        {existing && regenerating && (
          <div className="report-press__prompt">
            {passwordField}
            <button
              type="button"
              className="report-press__btn report-press__btn--primary"
              onClick={handleGenerate}
              disabled={!canGenerate || isGenerating || checkingExisting}
            >
              {isGenerating
                ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Generating…</>
                : <><i className="fas fa-rotate" />Regenerate report</>}
            </button>
            <button
              type="button"
              className="report-press__quiet"
              onClick={() => setRegenerating(false)}
              disabled={isGenerating}
            >
              Cancel
            </button>
          </div>
        )}

        {!existing && passwordOpen && passwordField}

        {changedFields.length > 0 && (
          <p className="report-press__hint">
            <i className="fas fa-circle-info" />
            Regenerate to apply your changes to {changedFields.map(f => f.label).join(', ')}
            {' '}— the stored PDF still uses the previous {changedFields.length === 1 ? 'value' : 'values'}.
          </p>
        )}

        {passwordChanged && (
          <p className="report-press__hint">
            <i className="fas fa-circle-info" />
            Regenerate to apply the new password — the stored PDF still uses the old one.
          </p>
        )}

        {error && (
          <p className="report-press__error"><i className="fas fa-circle-exclamation" />{error}</p>
        )}

        {/* The bar itself: last child, so it holds the same pixels open or
            shut. What it is and what state it's in stay legible while the
            panel is minimised, and the actions never move out from under the
            pointer that's reaching for them. */}
        <div className="report-press__row report-press__row--bar">
          <span className="report-press__eyebrow">Report PDF</span>
          {stateChip}
          {everydayActions && (
            <span className="report-press__bar-actions">{everydayActions}</span>
          )}
        </div>
      </div>

      {/* Pre-flight for the FIRST generation. Reuses the app's confirmation
          dialog (its body renders pre-line, so the summary can be laid out) and
          spends the cancel button on something useful: "Review details" jumps
          to the fields rather than merely dismissing. The X still plain-closes
          for anyone who just wants out. */}
      <ConfirmationModal
        show={showPreflight}
        title="Before you generate this report"
        // Bulleted, not indented: the dialog body is `white-space: pre-line`,
        // which keeps newlines but collapses runs of spaces, so leading indents
        // vanish and the summary reads as one flat block.
        message={[
          ...preflightNotes,
          '',
          'This report will contain:',
          `• ${publishedCount} published post${publishedCount === 1 ? '' : 's'}`,
          ...(periodLabel ? [`• ${periodLabel}`] : []),
        ].join('\n')}
        confirmText="Generate report"
        cancelText={offerFieldReview ? 'Review details' : 'Cancel'}
        onConfirm={() => void handleGenerate()}
        onCancel={() => {
          setShowPreflight(false);
          if (offerFieldReview) onEditFields!();
        }}
      />

      {viewing && existing && (
        <PdfViewer
          sourceKey={existing.url}
          title={[childName, periodLabel].filter(Boolean).join(' — ') || 'Report'}
          load={async () => ({ url: existing.url, password: existing.password })}
          missingHint="Regenerate the report to create a new PDF."
          onClose={() => setViewing(false)}
        />
      )}
    </div>
  );
};

export default GenerateReportPanel;
